import React, { useEffect, useState, useCallback } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { theme } from '../../../../config/theme';
import CheckoutConfirmation from '../../payment/CheckoutConfirmation';
import { StaxProvider } from '../../providers/StaxProvider';
import { staxConfig } from '../../../../config/stax';
import { useAuth } from '../../../contexts/ClerkAuthContext';
import { useCart } from '../../../contexts/CartContext';
import { userService } from '../../../api/migratedJuntoAPI';

const PageContainer = styled.div`
  min-height: 100vh;
  background: ${theme.colors.primary.background};
  padding-top: 80px;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 50vh;
`;

const LoadingSpinner = styled.div`
  width: 48px;
  height: 48px;
  border: 3px solid ${theme.colors.neutral.light};
  border-top-color: ${theme.colors.primary.buttons};
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const ErrorContainer = styled.div`
  max-width: 600px;
  margin: ${theme.spacing.xl} auto;
  padding: ${theme.spacing.xl};
  text-align: center;
`;

const ErrorMessage = styled.h2`
  font-family: ${theme.typography.primary.fontFamily};
  font-size: ${theme.typography.primary.heading.fontSize};
  color: ${theme.colors.primary.text};
  margin-bottom: ${theme.spacing.md};
`;

const ErrorDescription = styled.p`
  font-family: ${theme.typography.secondary.fontFamily};
  font-size: ${theme.typography.secondary.body.fontSize};
  color: ${theme.colors.neutral.medium};
  margin-bottom: ${theme.spacing.lg};
`;

const Button = styled.button`
  font-family: ${theme.typography.button.fontFamily};
  font-size: ${theme.typography.button.fontSize};
  font-weight: ${theme.typography.button.fontWeight};
  letter-spacing: ${theme.typography.button.letterSpacing};
  text-transform: ${theme.typography.button.textTransform};
  
  background: transparent;
  color: ${theme.colors.primary.buttons};
  border: 1px solid ${theme.colors.primary.buttons};
  border-radius: 50px;
  
  padding: ${theme.spacing.md} ${theme.spacing.lg};
  cursor: pointer;
  transition: ${theme.transitions.default};
  text-decoration: none;
  
  &:hover {
    background: ${theme.colors.primary.buttons};
    color: ${theme.colors.primary.background};
    transform: translateY(-2px);
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const CheckoutPage = () => {
  const navigate = useNavigate();
  const { isSignedIn, accountData } = useAuth();
  const { items: cartItems, clearCart } = useCart();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [paymentMethods, setPaymentMethods] = useState([]);

  const loadPaymentMethods = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Get payment methods from account data
      const methods = accountData?.wallet?.payment_methods || [];
      setPaymentMethods(methods);
    } catch (err) {
      console.error('Error loading payment methods:', err);
      setError('Failed to load payment methods');
    } finally {
      setIsLoading(false);
    }
  }, [accountData]);

  useEffect(() => {
    // Check if user is authenticated
    if (!isSignedIn) {
      navigate('/book', { state: { from: '/checkout' } });
      return;
    }

    // Check if cart has items
    if (!cartItems || cartItems.length === 0) {
      navigate('/cart');
      return;
    }

    // Load payment methods
    loadPaymentMethods();
  }, [isSignedIn, cartItems, navigate, loadPaymentMethods]);

  const handleProcessPayment = async (paymentData) => {
    try {
      // Process payment through Stax API
      const response = await userService.processStaxPayment(paymentData);
      
      if (response.success) {
        // Clear cart after successful payment
        clearCart();
        
        return {
          success: true,
          transaction_id: response.transaction_id
        };
      } else {
        return {
          success: false,
          error: response.error || 'Payment processing failed'
        };
      }
    } catch (error) {
      console.error('Payment processing error:', error);
      return {
        success: false,
        error: error.message || 'An error occurred during payment processing'
      };
    }
  };

  const handleCancel = () => {
    navigate('/cart');
  };

  // Transform cart items into order items format
  const orderItems = (cartItems || []).map(item => ({
    id: item.id,
    name: item.name || item.title,
    description: item.description,
    price: Math.round(item.price * 100), // Convert to cents
    quantity: item.quantity || 1,
    type: item.type
  }));

  if (isLoading) {
    return (
      <PageContainer>
        <LoadingContainer>
          <LoadingSpinner />
        </LoadingContainer>
      </PageContainer>
    );
  }

  if (error) {
    return (
      <PageContainer>
        <ErrorContainer>
          <ErrorMessage>Error Loading Checkout</ErrorMessage>
          <ErrorDescription>{error}</ErrorDescription>
          <Button onClick={() => navigate('/cart')}>
            Return to Cart
          </Button>
        </ErrorContainer>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <StaxProvider publicKey={staxConfig.publicKey} environment={staxConfig.environment}>
        <CheckoutConfirmation
          orderItems={orderItems}
          paymentMethods={paymentMethods}
          onProcessPayment={handleProcessPayment}
          onCancel={handleCancel}
          isLoading={isLoading}
          error={error}
        />
      </StaxProvider>
    </PageContainer>
  );
};

export default CheckoutPage;